import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Dropdown,
  Empty,
  Input,
  Menu,
  message,
  notification,
  Popconfirm,
  Popover,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
} from "antd";
import { DeleteOutlined, EditOutlined, FilterOutlined, PlusCircleOutlined, SearchOutlined } from "@ant-design/icons";
import AvatarStatus from "components/shared-components/AvatarStatus";
import EllipsisDropdown from "components/shared-components/EllipsisDropdown";
import Flex from "components/shared-components/Flex";
import { useNavigate } from "react-router-dom";
import utils from "utils";
import { useTranslation } from "react-i18next";
import { uniqueMergeArrays } from "functions/arrays";
import { deleteDoc, doc, updateDoc } from "firebase/firestore";
import { firestore } from "../../../../configs/FirebaseConfig";
import { deleteAssociatedFilesFromStorage } from "functions/firebase/storage";
import urls from "urls";
import { formatPrice } from "../../../../functions/formatters";
import Loading from "../../../../components/shared-components/Loading";
import _ from "lodash";
import {
  FIRESTORE_PRICE_LISTS_TABLE,
  FIRESTORE_PRODUCTS_TABLE,
  FIRESTORE_REGIONS_TABLE,
} from "../../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../../constants/CompanyConstants";
import dayjs from "dayjs";
import { PRODUCT_COMPANY_GROUPS, PRODUCT_GROUPS } from "../../../../constants/ProductConstant";
import { useProducts } from "../../../../data/hooks/firestore/useProducts";
import { usePriceLists } from "../../../../data/hooks/firestore/usePriceLists";
import { useAuth } from "../../../../data/hooks/firebase/useAuth";
import roles from "../../../../roles";
import { getUserPermissions } from "../../../../utils/permissionUtils";
import { StyledSelect } from "../../../../styles/sharedStyles";
import useIsMobile from "../../../../hooks/useIsMobile";
import styled from "styled-components";

const { Option } = Select;

const ProductList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { products, isLoading: productsLoading } = useProducts();
  const { priceLists } = usePriceLists();
  const { email } = useAuth();
  const [list, setList] = useState(products);
  const categories = uniqueMergeArrays(products, "productCategory");
  const [clickedCategory, setClickedCategory] = useState(localStorage.getItem("categoryFilter") || "All");
  const [tablePage, setTablePage] = useState(parseInt(localStorage.getItem("prdListPagination")) || 1);
  const [syncLoading, setSyncLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const { role: currentUserRole, uid } = useAuth();
  const [userPermissions, setUserPermissions] = useState({});
  const [clickedProductCompany, setClickedProductCompany] = useState(
    localStorage.getItem("productCompanyFilter") || "All",
  );
  const [clickedProductGroup, setClickedProductGroup] = useState(localStorage.getItem("productGroupFilter") || "All");
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false);
  const isMobileView = useIsMobile();

  useEffect(() => {
    const loadPermissions = async () => {
      if (currentUserRole === roles.superAdmin) {
        // SuperAdmin heeft alles
        setUserPermissions({ edit: true });
      } else if (currentUserRole === roles.admin) {
        const permissions = await getUserPermissions(uid, currentUserRole);
        setUserPermissions(permissions["shop-products-productList"] || { view: false, edit: false });
      }
    };
    loadPermissions();
  }, [currentUserRole, uid]);

  const hasEditPermission = currentUserRole === roles.superAdmin || userPermissions.edit === true;

  useEffect(() => {
    applyFilters(
      searchText,
      !!clickedCategory ? clickedCategory : "All",
      !!clickedProductCompany ? clickedProductCompany : "All",
      !!clickedProductGroup ? clickedProductGroup : "All",
    );
  }, [products]);

  const getProductStockColor = (stock) => {
    if (stock === false) return "red";
    else if (stock === true) return "green";
    else return "green";
  };

  const productStatusMenu = (product) => (
    <Menu>
      <Menu.Item disabled={!hasEditPermission || product.productGroup === "Bundles"}>
        <a onClick={(e) => updateProductStock(e, product, true)}>{t("inStock")}</a>
      </Menu.Item>
      <Menu.Item disabled={!hasEditPermission || product.productGroup === "Bundles"}>
        <a onClick={(e) => updateProductStock(e, product, false)}>{t("outOfStock")}</a>
      </Menu.Item>
    </Menu>
  );

  const productSeasonMenu = (product) => (
    <Menu>
      <Menu.Item disabled={!hasEditPermission || product.productGroup === "Bundles"}>
        <a onClick={(e) => updateProductInSeason(e, product, true)}>{t("in_season")}</a>
      </Menu.Item>
      <Menu.Item disabled={!hasEditPermission || product.productGroup === "Bundles"}>
        <a onClick={(e) => updateProductInSeason(e, product, false)}>{t("out_of_season")}</a>
      </Menu.Item>
    </Menu>
  );

  const dropdownMenu = (row) => (
    <Menu>
      <Menu.Item disabled={!hasEditPermission} onClick={() => viewDetails(row)}>
        <Flex alignItems="center">
          <EditOutlined />
          <span style={{ marginLeft: 4 }}>{t("edit")}</span>
        </Flex>
      </Menu.Item>
      <Menu.Item disabled={!hasEditPermission}>
        <Popconfirm
          placement="bottom"
          title={t("confirm_delete_product", { name: row.productName })}
          onConfirm={() => beforeDeleteRow(row)}
          okText={t("yes")}
          cancelText={t("no")}
          disabled={!hasEditPermission}
        >
          <a onClick={(e) => !hasEditPermission && e.preventDefault()}>
            <DeleteOutlined />
            {t("delete")}
          </a>
        </Popconfirm>
      </Menu.Item>
    </Menu>
  );

  const addProduct = () => {
    navigate(urls.addProduct);
  };

  const viewDetails = (row) => {
    navigate(urls.editProduct + row.id);
  };

  const beforeDeleteRow = (row) => {
    let associatedProducts = _.filter(products, {
      productItems: [{ productId: row.id }],
    });
    if (associatedProducts.length > 0) {
      let itemsString = "";
      _.forEach(associatedProducts, (item) => (itemsString += item.name + "\n"));
      notification.open({
        message: t("notifications.linked_products_title"),
        description: t("notifications.linked_products_description") + itemsString,
        duration: 0,
        key: row.id,
        btn: (
          <Button
            type="primary"
            onClick={() => {
              deleteRow(row);
              notification.close(row.id);
            }}
          >
            {t("proceed")}
          </Button>
        ),
      });
    } else deleteRow(row);
  };

  const deleteRow = (row) => {
    const objKey = "id";
    let data = list;
    let images = row.productImage512DownloadURL;
    deleteAssociatedFilesFromStorage(images);
    const productDocRef = doc(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_PRODUCTS_TABLE,
      row.id,
    );
    deleteDoc(productDocRef)
      .then(() => {
        data = utils.deleteArrayRow(data, objKey, row.id);
        setList(data);
        message.success(t("notifications.product_deleted", 4));
        deleteAssociatedProductItems(row.id);
        deleteAssociatedPriceListEntries(row.id);
      })
      .catch((error) => {
        message.error(error.message, 4);
      });
  };

  const deleteAssociatedProductItems = (id) => {
    let associatedProducts = _.filter(products, {
      productItems: [{ productId: id }],
    });
    if (associatedProducts.length > 0) {
      _.forEach(associatedProducts, (product) => {
        let productItems = product.productItems;
        let remainingItems = _.filter(productItems, (item) => {
          return item.productId !== id;
        });

        if (remainingItems.length === 0) remainingItems = null;
        const productDocRef = doc(
          firestore,
          FIRESTORE_REGIONS_TABLE,
          COMPANY_COUNTRY_CODE,
          FIRESTORE_PRODUCTS_TABLE,
          product.id,
        );
        updateDoc(productDocRef, {
          productItems: remainingItems,
          updatedAt: dayjs().unix(),
          updatedBy: email,
        });
      });
    }
  };

  // Remove this product's custom price entry from every price list (country and customer) that has one,
  // so a deleted product doesn't leave stale customPrices entries behind.
  const deleteAssociatedPriceListEntries = (id) => {
    const associatedPriceLists = priceLists.filter((pl) => pl.customPrices?.[id] !== undefined);

    associatedPriceLists.forEach((priceList) => {
      const updatedCustomPrices = { ...priceList.customPrices };
      delete updatedCustomPrices[id];

      const priceListDocRef = doc(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        COMPANY_COUNTRY_CODE,
        FIRESTORE_PRICE_LISTS_TABLE,
        priceList.id,
      );
      updateDoc(priceListDocRef, {
        customPrices: updatedCustomPrices,
        updatedAt: dayjs().unix(),
        updatedBy: email,
      }).catch((error) => {
        console.error(`Error removing product ${id} from price list ${priceList.id}`, error);
      });
    });
  };

  const updateProductStock = (e, product, stock) => {
    e.preventDefault();
    const productDocRef = doc(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_PRODUCTS_TABLE,
      product.productId,
    );
    updateDoc(productDocRef, {
      productInStock: stock,
      updatedAt: dayjs().unix(),
      updatedBy: email,
    })
      .then(() => message.success(t("stock_changed_success")))
      .catch(() => message.error(t("stock_changed_failure")));
  };

  const updateProductInSeason = (e, product, inSeason) => {
    e.preventDefault();
    const productDocRef = doc(
      firestore,
      FIRESTORE_REGIONS_TABLE,
      COMPANY_COUNTRY_CODE,
      FIRESTORE_PRODUCTS_TABLE,
      product.productId,
    );
    updateDoc(productDocRef, {
      productInSeason: inSeason,
      updatedAt: dayjs().unix(),
      updatedBy: email,
    })
      .then(() => message.success(t("season_changed_success")))
      .catch(() => message.error(t("season_changed_failure")));
  };

  const tableColumns = [
    {
      title: t("product"),
      dataIndex: "productId",
      render: (_, record) => (
        <div className="d-flex" onClick={() => viewDetails(record)}>
          <AvatarStatus
            size={60}
            type="square"
            src={!!record.productImage512DownloadURL ? record.productImage512DownloadURL[0] : null}
            name={record.productId}
            linkTo={urls.editProduct + record.id}
          />
        </div>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "productId"),
    },
    {
      title: t("stock"),
      dataIndex: "productInStock",
      render: (_, product) => (
        <>
          <Tooltip title={product.productGroup === "Bundles" ? t("bundle_stock_managed_by_items") : ""}>
            <Dropdown
              overlay={productStatusMenu(product)}
              trigger={["click"]}
              disabled={product.productGroup === "Bundles"}
            >
              <a onClick={(e) => e.preventDefault()}>
                <Tag color={getProductStockColor(product.productInStock)}>
                  {product.productInStock ? t("inStock") : t("outOfStock")}
                </Tag>
              </a>
            </Dropdown>
          </Tooltip>
        </>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "productInStock"),
    },
    {
      title: t("season"),
      dataIndex: "productInSeason",
      render: (_, product) => (
        <>
          <Tooltip title={product.productGroup === "Bundles" ? t("bundle_season_managed_by_items") : ""}>
            <Dropdown
              overlay={productSeasonMenu(product)}
              trigger={["click"]}
              disabled={product.productGroup === "Bundles"}
            >
              <a onClick={(e) => e.preventDefault()}>
                <Tag color={getProductStockColor(product.productInSeason)}>
                  {product.productInSeason ? t("in_season") : t("out_of_season")}
                </Tag>
              </a>
            </Dropdown>
          </Tooltip>
        </>
      ),
      sorter: (a, b) => utils.antdTableSorter(a, b, "productInStock"),
    },
    {
      title: t("name"),
      dataIndex: "productName",
      render: (productNameNL, record) => <div onClick={() => viewDetails(record)}>{productNameNL}</div>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "productName"),
      defaultSortOrder: "descend",
    },
    {
      title: t("categories"),
      dataIndex: "productCategory",
      render: (productCategoryNL) => <div>{productCategoryNL}</div>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "productCategory"),
    },
    {
      title: t("price"),
      dataIndex: "price",
      render: (price) => <div>€{formatPrice(price)}</div>,
      sorter: (a, b) => utils.antdTableSorter(a, b, "price"),
    },
    {
      title: t("vat"),
      dataIndex: "vat",
      render: (vat) => {
        if (vat && vat.nl !== null)
          return (
            <span>{vat.nl}%</span>
            // DISABLED FOR NOW - MIGHT BE NEEDED IN FUTURE
            // <Popover
            //   content={
            //     <ul style={{ listStyle: "none", margin: "0", padding: "0" }}>
            //       {Object.entries(vat).map(([country, rate]) => {
            //         if (rate)
            //           return (
            //             <li key={country} style={{ margin: 0 }}>
            //               <p>
            //                 <strong>{country.toUpperCase()}: </strong>
            //                 {rate}%
            //               </p>
            //             </li>
            //           );
            //       })}
            //     </ul>
            //   }
            // >
            //   {vat.nl}%
            // </Popover>
          );
      },
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_, elm) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(elm)} />
        </div>
      ),
    },
  ];

  const comparePricesFromSnelstart = (snelstartProducts) => {
    const timestamp = dayjs().unix();
    products.forEach((product, index) => {
      const snelstartProduct = snelstartProducts.find(
        (snelstartProduct) => snelstartProduct.id === product.snelstartArtikelId,
      );
      if (snelstartProduct) {
        updateProductPrice(product.price, snelstartProduct.verkoopprijs, product.productId, timestamp)
          .then(() => {
            if (index === products.length - 1) {
              setSyncLoading(false);
              message.success(t("notifications.prices_updated"));
            }
          })
          .catch((err) => {
            console.log(err);
          });
      }
    });
  };

  const updateProductPrice = (oldPrice, newPrice, productId, timestamp) => {
    return new Promise((resolve, reject) => {
      if (oldPrice !== newPrice) {
        const productDocRef = doc(
          firestore,
          FIRESTORE_REGIONS_TABLE,
          COMPANY_COUNTRY_CODE,
          FIRESTORE_PRODUCTS_TABLE,
          productId,
        );
        updateDoc(productDocRef, {
          price: newPrice,
          priceSyncedWithSnelstart: timestamp,
          updatedAt: dayjs().unix(),
          updatedBy: email,
        })
          .then(() => resolve())
          .catch((err) => reject(err));
      } else resolve();
    });
  };

  const onSearch = (value) => {
    setSearchText(value);
    applyFilters(value, clickedCategory, clickedProductCompany, clickedProductGroup);
  };

  const handleShowCategory = (value) => {
    localStorage.setItem("categoryFilter", value);
    localStorage.setItem("prdListPagination", 1);
    setClickedCategory(value);
    applyFilters(searchText, value, clickedProductCompany, clickedProductGroup);
  };

  const handleShowProductGroup = (value) => {
    localStorage.setItem("productGroupFilter", value);
    localStorage.setItem("prdListPagination", 1);
    setClickedProductGroup(value);
    applyFilters(searchText, clickedCategory, clickedProductCompany, value);
  };

  const handleShowProductCompany = (value) => {
    localStorage.setItem("productCompanyFilter", value);
    localStorage.setItem("prdListPagination", 1);
    setClickedProductCompany(value);
    applyFilters(searchText, clickedCategory, value, clickedProductGroup);
  };

  const applyFilters = (searchValue, categoryValue, productCompanyValue, productGroupValue) => {
    let filteredData = products;

    // Filter by category
    if (categoryValue !== "All") {
      filteredData = utils.filterString(filteredData, "productCategory", categoryValue);
    }

    // Filter by product company
    if (productCompanyValue !== "All") {
      filteredData = filteredData.filter((product) => product.company === productCompanyValue);
    }

    // Filter by product group
    if (productGroupValue !== "All") {
      filteredData = filteredData.filter((product) => product.productGroup === productGroupValue);
    }

    // Apply search
    if (searchValue) {
      filteredData = utils.wildCardSearchProduct(filteredData, searchValue, "All");
    }

    setList(filteredData);
  };

  const onChangeTablePage = (value) => {
    localStorage.setItem("prdListPagination", value);
    setTablePage(value);
  };

  return (
    <Card>
      <Flex alignItems="center" justifyContent="between" mobileFlex={true}>
        {!isMobileView ? (
          <Space wrap style={{ marginBottom: 8 }}>
            <Input
              value={searchText}
              placeholder={t("search")}
              prefix={<SearchOutlined />}
              onChange={(e) => onSearch(e.target.value)}
              allowClear={true}
            />
            <StyledSelect
              className="w-100"
              style={{ minWidth: 140 }}
              $isFiltered={clickedProductCompany !== "All"}
              onChange={handleShowProductCompany}
              placeholder={t("company")}
              defaultValue={clickedProductCompany !== "null" ? clickedProductCompany : "All"}
            >
              <Option value="All">{t("all_companies")}</Option>
              {PRODUCT_COMPANY_GROUPS.map((group) => (
                <Option key={group.value} value={group.value}>
                  {group.label}
                </Option>
              ))}
            </StyledSelect>
            <StyledSelect
              className="w-100"
              style={{ minWidth: 140 }}
              $isFiltered={clickedProductGroup !== "All"}
              onChange={handleShowProductGroup}
              placeholder={t("product_group")}
              defaultValue={clickedProductGroup !== "null" ? clickedProductGroup : "All"}
            >
              <Option value="All">{t("all_groups")}</Option>
              {PRODUCT_GROUPS.map((group) => (
                <Option key={group.value} value={group.value}>
                  {t(group.label)}
                </Option>
              ))}
            </StyledSelect>
            <StyledSelect
              className="w-100"
              style={{ minWidth: 140 }}
              $isFiltered={clickedCategory !== "All"}
              onChange={handleShowCategory}
              placeholder={t("category")}
              defaultValue={clickedCategory !== "null" ? clickedCategory : "All"}
            >
              <Option value="All">{t("all_categories")}</Option>
              {!!categories &&
                categories.map((elm) => (
                  <Option key={elm} value={elm}>
                    {elm}
                  </Option>
                ))}
            </StyledSelect>
          </Space>
        ) : (
          <Popover
            content={
              <FilterPopoverContent>
                <div>
                  <FilterLabel>{t("search")}</FilterLabel>
                  <Input
                    value={searchText}
                    placeholder={t("search")}
                    prefix={<SearchOutlined />}
                    allowClear
                    onChange={(e) => onSearch(e.target.value)}
                    size="large"
                  />
                </div>
                <div>
                  <FilterLabel>{t("company")}</FilterLabel>
                  <Select
                    placeholder={t("company")}
                    style={{ width: "100%" }}
                    value={clickedProductCompany !== "null" ? clickedProductCompany : "All"}
                    onChange={handleShowProductCompany}
                    size="large"
                  >
                    <Option value="All">{t("all_companies")}</Option>
                    {PRODUCT_COMPANY_GROUPS.map((group) => (
                      <Option key={group.value} value={group.value}>
                        {group.label}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <FilterLabel>{t("product_group")}</FilterLabel>
                  <Select
                    placeholder={t("product_group")}
                    style={{ width: "100%" }}
                    value={clickedProductGroup !== "null" ? clickedProductGroup : "All"}
                    onChange={handleShowProductGroup}
                    size="large"
                  >
                    <Option value="All">{t("all_groups")}</Option>
                    {PRODUCT_GROUPS.map((group) => (
                      <Option key={group.value} value={group.value}>
                        {t(group.label)}
                      </Option>
                    ))}
                  </Select>
                </div>
                <div>
                  <FilterLabel>{t("category")}</FilterLabel>
                  <Select
                    placeholder={t("category")}
                    style={{ width: "100%" }}
                    value={clickedCategory !== "null" ? clickedCategory : "All"}
                    onChange={handleShowCategory}
                    size="large"
                  >
                    <Option value="All">{t("all_categories")}</Option>
                    {!!categories &&
                      categories.map((elm) => (
                        <Option key={elm} value={elm}>
                          {elm}
                        </Option>
                      ))}
                  </Select>
                </div>
                <FilterButtonsContainer>
                  <Button
                    onClick={() => {
                      localStorage.removeItem("categoryFilter");
                      localStorage.removeItem("productCompanyFilter");
                      localStorage.removeItem("productGroupFilter");
                      setSearchText("");
                      setClickedProductCompany("All");
                      setClickedProductGroup("All");
                      setClickedCategory("All");
                      applyFilters("", "All", "All", "All");
                      setFilterPopoverVisible(false);
                    }}
                    style={{ flex: 1 }}
                  >
                    {t("reset")}
                  </Button>
                  <Button type="primary" onClick={() => setFilterPopoverVisible(false)} style={{ flex: 1 }}>
                    {t("apply")}
                  </Button>
                </FilterButtonsContainer>
              </FilterPopoverContent>
            }
            title={t("filters")}
            trigger="click"
            open={filterPopoverVisible}
            onOpenChange={setFilterPopoverVisible}
            placement="bottomLeft"
          >
            <Button
              icon={<FilterOutlined />}
              style={{
                borderColor:
                  clickedProductCompany !== "All" ||
                  clickedProductGroup !== "All" ||
                  clickedCategory !== "All" ||
                  searchText
                    ? "#FF4B33"
                    : "",
                color:
                  clickedProductCompany !== "All" ||
                  clickedProductGroup !== "All" ||
                  clickedCategory !== "All" ||
                  searchText
                    ? "#FF4B33"
                    : "",
                marginBottom: 8,
              }}
            />
          </Popover>
        )}
        <Space wrap style={{ marginBottom: 8 }}>
          {/*<Button onClick={handleClickSyncPricesSnelstart} block loading={syncLoading}>*/}
          {/*  {t("sync_prices_snelstart")}*/}
          {/*</Button>*/}
          <Button
            onClick={addProduct}
            type="primary"
            icon={<PlusCircleOutlined />}
            block
            className="w-auto"
            disabled={!hasEditPermission}
          >
            {t("add_product")}
          </Button>
        </Space>
      </Flex>
      {productsLoading ? (
        <Loading />
      ) : (
        <div className="table-responsive">
          <Table
            showSorterTooltip={false}
            columns={tableColumns}
            dataSource={list}
            rowKey="id"
            pagination={{
              current: !!tablePage ? tablePage : 1,
              onChange: onChangeTablePage,
            }}
            locale={{
              emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("table_no_products")} />,
            }}
            loading={productsLoading}
          />
        </div>
      )}
    </Card>
  );
};

export default ProductList;

const FilterPopoverContent = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FilterLabel = styled.label`
  display: block;
  margin-bottom: 4px;
  font-weight: 500;
`;

const FilterButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 4px;
  border-top: 1px solid #f0f0f0;
  margin-top: 4px;
`;
